// Shared All-tab toolbar — CTSK-466 (PatsPanel + ExternalClientsNest
// table parity). Right-aligned row of: search box · Kind dropdown ·
// State dropdown · "Show archived" toggle. Presentational — filter
// state + semantics live with the consumer (the two tables' Kind and
// State vocabularies are different domains: PAT lifecycle vs access-
// request workflow — deliberately NOT unified into one enum).
//
// URL persistence: consumers seed from readUrlParam and mirror via
// writeUrlParam (?q=&kind=&state=&archived=1) — same replaceState
// convention as lib/use-list-search.ts, preserving foreign params
// (Settings' ?section=, ECN's ?tab=, the shared ?sort/&dir).

import type { ReactNode } from 'react';

export interface ToolbarOption {
  value: string;
  label: string;
}

/** Read one query param ('' when absent). */
export function readUrlParam(key: string): string {
  if (typeof window === 'undefined') return '';
  return new URLSearchParams(window.location.search).get(key) ?? '';
}

/** Set/clear one query param via replaceState. Empty/null clears. */
export function writeUrlParam(key: string, value: string | null): void {
  if (typeof window === 'undefined') return;
  const sp = new URLSearchParams(window.location.search);
  if (value) sp.set(key, value);
  else sp.delete(key);
  const qs = sp.toString();
  window.history.replaceState(null, '', window.location.pathname + (qs ? `?${qs}` : ''));
}

/** Chip-click filter wrapper — CTSK-466 slice 4. Wraps a State/Kind
 *  chip in a button that toggles the corresponding dropdown filter
 *  (click again to clear). Active filter renders an accent ring.
 *  Consumers render it only where the filter exists (the All tab) —
 *  on single-state tabs the chips stay inert. stopPropagation keeps
 *  the row-level click-through (doc detail / detail toast) intact. */
export function ChipFilterButton({
  active,
  title,
  onToggle,
  children,
}: {
  active: boolean;
  title: string;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      title={title}
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        background: 'transparent',
        border: active ? '1px solid var(--accent-primary)' : '1px solid transparent',
        borderRadius: 12,
        padding: 1,
        cursor: 'pointer',
        font: 'inherit',
        color: 'inherit',
      }}
    >
      {children}
    </button>
  );
}

const selectStyle = {
  padding: '4px 8px',
  fontSize: 12,
  border: '1px solid var(--border-strong)',
  borderRadius: 4,
  background: 'var(--surface-raised)',
  color: 'var(--text-primary)',
} as const;

export function ToolbarRow({
  search,
  onSearchChange,
  searchPlaceholder = 'Search…',
  kindOptions,
  kind,
  onKindChange,
  stateOptions,
  state,
  onStateChange,
  showArchived,
  onShowArchivedChange,
  archivedHiddenCount = 0,
}: {
  search: string;
  onSearchChange: (v: string) => void;
  searchPlaceholder?: string;
  kindOptions: readonly ToolbarOption[];
  /** '' = all kinds. */
  kind: string;
  onKindChange: (v: string) => void;
  stateOptions: readonly ToolbarOption[];
  /** '' = all states. */
  state: string;
  onStateChange: (v: string) => void;
  showArchived: boolean;
  onShowArchivedChange: (v: boolean) => void;
  /** Rows the archive filter is currently hiding (0 while showing). */
  archivedHiddenCount?: number;
}) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: 10,
        flexWrap: 'wrap',
        marginBottom: 10,
      }}
    >
      <input
        type="search"
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder={searchPlaceholder}
        aria-label="Search rows"
        style={{
          padding: '4px 10px',
          fontSize: 12,
          border: '1px solid var(--border-strong)',
          borderRadius: 4,
          minWidth: 180,
        }}
      />
      <select
        value={kind}
        onChange={(e) => onKindChange(e.target.value)}
        aria-label="Filter by kind"
        style={selectStyle}
      >
        <option value="">All kinds</option>
        {kindOptions.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <select
        value={state}
        onChange={(e) => onStateChange(e.target.value)}
        aria-label="Filter by state"
        style={selectStyle}
      >
        <option value="">All states</option>
        {stateOptions.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <label
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          fontSize: 12,
          color: 'var(--text-secondary)',
          whiteSpace: 'nowrap',
          cursor: 'pointer',
        }}
        title="Archived = revoked/denied more than 30 days ago, or long-expired. Hidden by default."
      >
        <input
          type="checkbox"
          checked={showArchived}
          onChange={(e) => onShowArchivedChange(e.target.checked)}
        />
        Show archived
        {!showArchived && archivedHiddenCount > 0 && (
          <span style={{ color: 'var(--text-muted)' }}>({archivedHiddenCount} hidden)</span>
        )}
      </label>
    </div>
  );
}
